/**
 * Flow 7 — sync. "Did the structure we shipped actually work."
 *
 * Metrics are per project, normalised across platforms, and only appear once a push
 * reached `live_paused`: an account with nothing in it has no performance to report, and
 * showing zeros would be a lie dressed as data.
 */
import { useMemo, useState } from 'react'
import { useMetrics } from '@/api/hooks'
import type { MetricRow } from '@/api/types'
import { formatMoneyMicros, formatNumber, formatPercent, platformLabel } from '@/lib/format'
import { Badge, EmptyState, ErrorNotice, Panel, Select, Spinner, StatTile } from '@/components/ui'

function isoDaysAgo(days: number): string {
  const date = new Date()
  date.setUTCDate(date.getUTCDate() - days)
  return date.toISOString().slice(0, 10)
}

function summarise(rows: MetricRow[]) {
  const impressions = rows.reduce((sum, row) => sum + row.impressions, 0)
  const clicks = rows.reduce((sum, row) => sum + row.clicks, 0)
  const cost = rows.reduce((sum, row) => sum + row.cost_micros, 0)
  const conversions = rows.reduce((sum, row) => sum + row.conversions, 0)
  const value = rows.reduce((sum, row) => sum + row.conversion_value_micros, 0)
  return {
    impressions,
    clicks,
    cost,
    conversions,
    value,
    ctr: impressions === 0 ? 0 : clicks / impressions,
    cpc: clicks === 0 ? 0 : Math.round(cost / clicks),
    cpa: conversions === 0 ? null : Math.round(cost / conversions),
    roas: cost === 0 ? null : value / cost,
  }
}

export function MetricsPanel({ projectId }: { projectId: string }) {
  const [level, setLevel] = useState<'campaign' | 'ad_group' | 'keyword'>('ad_group')
  const [windowDays, setWindowDays] = useState(30)
  const [platform, setPlatform] = useState<'all' | 'google' | 'microsoft'>('all')

  const query = useMemo(
    () => ({ start_date: isoDaysAgo(windowDays - 1), end_date: isoDaysAgo(0), level }),
    [windowDays, level],
  )
  const metrics = useMetrics(projectId, query)

  const rows = (metrics.data?.rows ?? []).filter((row) =>
    platform === 'all' ? true : row.platform === platform,
  )
  const summary = summarise(rows)

  return (
    <div className="flex flex-col gap-4" data-testid="metrics-panel">
      <Panel
        title="Performance"
        subtitle={`${query.start_date} → ${query.end_date} · one batched query per account per day, because reporting draws on the same operation quota`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Select
              aria-label="Entity level"
              data-testid="metrics-level"
              value={level}
              onChange={(event) => setLevel(event.target.value as typeof level)}
              className="w-[150px] py-1 text-[12px]"
            >
              <option value="campaign">Campaign level</option>
              <option value="ad_group">Ad group level</option>
              <option value="keyword">Keyword level</option>
            </Select>
            <Select
              aria-label="Window"
              data-testid="metrics-window"
              value={String(windowDays)}
              onChange={(event) => setWindowDays(Number(event.target.value))}
              className="w-[130px] py-1 text-[12px]"
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
            </Select>
            <Select
              aria-label="Platform"
              data-testid="metrics-platform"
              value={platform}
              onChange={(event) => setPlatform(event.target.value as typeof platform)}
              className="w-[150px] py-1 text-[12px]"
            >
              <option value="all">Both platforms</option>
              <option value="google">Google Ads</option>
              <option value="microsoft">Microsoft Ads</option>
            </Select>
          </div>
        }
      >
        {metrics.isLoading ? (
          <Spinner label="Loading metrics…" />
        ) : metrics.isError ? (
          <ErrorNotice error={metrics.error} />
        ) : rows.length === 0 ? (
          <EmptyState
            title="No metrics for this window"
            body="Reporting only exists once a push reached live (paused): an account with nothing in it has no performance to report. Zeros would be a lie dressed as data."
          />
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              <StatTile label="Spend" value={formatMoneyMicros(summary.cost, rows[0]?.currency_code ?? 'GBP')} />
              <StatTile label="Impressions" value={formatNumber(summary.impressions)} />
              <StatTile label="Clicks" value={formatNumber(summary.clicks)} />
              <StatTile label="CTR" value={formatPercent(summary.ctr)} />
              <StatTile label="Conversions" value={summary.conversions.toFixed(2)} />
              <StatTile
                label="CPA"
                value={summary.cpa === null ? '—' : formatMoneyMicros(summary.cpa, rows[0]?.currency_code ?? 'GBP')}
                tone={summary.cpa === null ? 'warn' : 'neutral'}
              />
            </div>

            <div className="mt-4 -mx-4 overflow-x-auto px-1">
              <table className="w-full min-w-[900px] border-collapse text-left">
                <thead>
                  <tr className="border-b border-ink-700">
                    {[
                      'Platform',
                      'Date',
                      'Campaign',
                      'Ad group',
                      'Impr.',
                      'Clicks',
                      'CTR',
                      'Avg CPC',
                      'Cost',
                      'Conv.',
                      'Invalid clicks',
                    ].map((heading) => (
                      <th key={heading} scope="col" className="label-cap px-2.5 py-2">
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    <tr
                      key={`${row.platform}-${row.utc_date}-${row.ad_group_external_id ?? row.campaign_external_id}-${index}`}
                      data-testid="metric-row"
                      className="border-b border-ink-800 last:border-b-0 hover:bg-ink-850/60"
                    >
                      <td className="px-2.5 py-2">
                        <Badge tone={row.platform === 'google' ? 'info' : 'neutral'}>
                          {row.platform === 'google' ? 'Google' : 'Microsoft'}
                        </Badge>
                      </td>
                      <td className="num px-2.5 py-2 text-[12px] text-mist-400">{row.utc_date}</td>
                      <td className="px-2.5 py-2 text-[12px] text-mist-200">{row.campaign_name ?? '—'}</td>
                      <td className="px-2.5 py-2 text-[12px] text-mist-200">{row.ad_group_name ?? '—'}</td>
                      <td className="num px-2.5 py-2 text-right text-[12px] text-mist-300">
                        {formatNumber(row.impressions)}
                      </td>
                      <td className="num px-2.5 py-2 text-right text-[12px] text-mist-300">
                        {formatNumber(row.clicks)}
                      </td>
                      <td className="num px-2.5 py-2 text-right text-[12px] text-mist-300">
                        {formatPercent(row.ctr)}
                      </td>
                      <td className="num px-2.5 py-2 text-right text-[12px] text-mist-300">
                        {formatMoneyMicros(row.average_cpc_micros, row.currency_code ?? 'GBP')}
                      </td>
                      <td className="num px-2.5 py-2 text-right text-[12px] text-mist-100">
                        {formatMoneyMicros(row.cost_micros, row.currency_code ?? 'GBP')}
                      </td>
                      <td className="num px-2.5 py-2 text-right text-[12px] text-mist-300">
                        {row.conversions.toFixed(2)}
                      </td>
                      <td className="num px-2.5 py-2 text-right text-[12px] text-mist-400">
                        {row.invalid_click_rate === null ? '—' : formatPercent(row.invalid_click_rate)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="mt-2 text-[11.5px] text-mist-500">
              Synced {metrics.data?.synced_at ?? 'never'} · {rows.length} rows · level {level} ·
              platforms: {platform === 'all' ? 'both' : platformLabel(platform)}
            </p>
          </>
        )}
      </Panel>

      {metrics.data && (metrics.data.rows ?? []).length > 0 ? (
        <Panel title="What this is for" testId="metrics-footnote">
          <p className="text-[12.5px] text-mist-400">
            Rows are attributed to the Blueprint version that produced the structure, which is what makes
            "did the ad groups we shipped work" answerable rather than a guess.
            {(metrics.data.rows ?? [])[0]?.blueprint_version
              ? ` These rows came from v${(metrics.data.rows ?? [])[0]?.blueprint_version}.`
              : ''}{' '}
            Invalid-click rate is captured even though v1 does not act on it — the data will be there when
            it becomes a feature.
          </p>
        </Panel>
      ) : null}
    </div>
  )
}
